import { formatNumber, formatDate, toMachineDate } from '@shared/format';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { DocumentIcon } from '@shared/ui/icons/nav';
import { RowMenu } from '@shared/ui/rowmenu';
import { StateBadge } from '@shared/ui/StateBadge';

import { contentTypeLabel, roleLabel } from '../_shared/copy';
import { distinctTitleCount } from '../_shared/components';
import type { CreditedItemRow } from '../_shared/types';

import { copy } from './copy';

const TABLE_COLUMNS = 5;

/**
 * The credit relations, one row per relation — **not one per title**.
 *
 * A title carrying two of this person's credits occupies two rows. Collapsing
 * them onto one row with two role badges would match the public page card for
 * card and lose the thing this table exists to state — that a person holds a
 * *named* role on a *named* title, and that the pair is the record.
 *
 * **Read-only, with one action: open the title.** The relation is authored on
 * the title's own edit page, next to the register and the role vocabulary, so
 * the row menu here points at it rather than duplicating it.
 *
 * **Neither count is written into the markup.** Both are read off what came
 * back, because a hand-written total under a table that edits itself is a lie
 * waiting for its first edit.
 *
 * **The publish date is the digit boundary in miniature.** The `datetime`
 * attribute carries Latin ISO-8601 for the machine and the text carries
 * Arabic-Indic for the reader; the two never meet inside one string, and
 * nothing parses the rendered numeral back into a date.
 */
export function PersonDetailCredits({
  items,
  pending,
  hasMore,
  personName,
  onOpenTitle,
}: {
  items: readonly CreditedItemRow[];
  pending: boolean;
  hasMore: boolean;
  personName: string;
  onOpenTitle: (row: CreditedItemRow) => void;
}) {
  if (pending) {
    return (
      <>
        <p role="status" className="sr-only">
          {copy.credits.states.loading}
        </p>
        <CreditsTable personName={personName}>
          <RowSkeleton columns={TABLE_COLUMNS} />
        </CreditsTable>
      </>
    );
  }

  if (items.length === 0) {
    return <EmptyState message={copy.credits.states.empty} />;
  }

  return (
    <>
      <CreditsTable personName={personName}>
        {items.map((row) => (
          // A title with two roles yields two rows carrying the same content
          // item id, so the key is the relation rather than the title.
          <tr key={`${row.id}:${row.role}`}>
            <DTableRowHeader>{row.title}</DTableRowHeader>

            <td>
              <StateBadge>{contentTypeLabel(row.content_type)}</StateBadge>
            </td>

            <td>
              <StateBadge>{roleLabel(row.role)}</StateBadge>
            </td>

            <td className="whitespace-nowrap">
              {row.published_at ? (
                <time dateTime={toMachineDate(row.published_at)}>
                  <bdi>{formatDate(row.published_at)}</bdi>
                </time>
              ) : (
                <span className="text-xs text-ink-muted">{copy.credits.unpublished}</span>
              )}
            </td>

            <td className="w-[3.5rem]">
              <RowMenu
                label={copy.credits.rowMenu.label(roleLabel(row.role), row.title)}
                entries={[
                  {
                    key: 'open',
                    label: copy.credits.rowMenu.openTitle,
                    icon: <DocumentIcon />,
                    onSelect: () => onOpenTitle(row),
                  },
                ]}
              />
            </td>
          </tr>
        ))}
      </CreditsTable>

      <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
        <bdi>{formatNumber(items.length)}</bdi> {copy.credits.count.creditsWord}{' '}
        <bdi>{formatNumber(distinctTitleCount(items))}</bdi> {copy.credits.count.titlesWord}{' '}
        {copy.credits.count.note}
        {hasMore ? ` ${copy.credits.count.more}` : null}
      </p>
    </>
  );
}

/**
 * The table frame, shared by the skeleton and the data states so neither
 * reflows the other. Five columns and a `40rem` floor.
 */
function CreditsTable({ personName, children }: { personName: string; children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.credits.caption(personName)} minInlineSize="40rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.credits.columns.title}</th>
            <th scope="col">{copy.credits.columns.type}</th>
            <th scope="col">{copy.credits.columns.role}</th>
            <th scope="col">{copy.credits.columns.publishedAt}</th>
            <th scope="col">
              <span className="sr-only">{copy.credits.columns.actions}</span>
            </th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}
